import React, { useState } from 'react';
import { Divider, Modal, Skeleton, Tag, Tooltip, Spin, App } from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  GlobalOutlined,
  MedicineBoxOutlined,
  FilePdfOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ThemeButton, VaccinationStatusBadge } from '../components';
import {
  useDeleteDogMutation,
  useRestoreDogMutation,
} from '../../services/dogsApi';

dayjs.extend(relativeTime);

const DogByIDModal = ({
  isOpen,
  onClose,
  data,
  isLoading,
  onEditDog,
  onViewOwner,
  onDeleteSuccess,
  zIndex,
}) => {
  const { message } = App.useApp();
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [restoreConfirmVisible, setRestoreConfirmVisible] = useState(false);
  const [deleteDog, { isLoading: deleting }] = useDeleteDogMutation();
  const [restoreDog, { isLoading: restoring }] = useRestoreDogMutation();

  // Reset delete confirmation when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setDeleteConfirmVisible(false);
      setRestoreConfirmVisible(false);
    }
  }, [isOpen]);

  // Delete dog handler
  const handleDelete = async () => {
    try {
      const result = await deleteDog(data._id).unwrap();
      if (result.error === true) {
        message.error(result.msg || 'Failed to delete dog');
        setDeleteConfirmVisible(false);
      } else {
        message.success(result.msg || 'Dog deleted successfully');
        // Close both modals with slight delay to ensure proper cleanup
        setDeleteConfirmVisible(false);
        setTimeout(() => {
          onClose();
          onDeleteSuccess?.();
        }, 100);
      }
    } catch (error) {
      message.error(error.data?.msg || 'Error deleting dog');
      console.error('Delete error:', error);
      setDeleteConfirmVisible(false);
    }
  };

  // Restore dog handler
  const handleRestore = async () => {
    try {
      const result = await restoreDog(data._id).unwrap();
      if (result.error === true) {
        message.error(result.msg || 'Failed to restore dog');
        setRestoreConfirmVisible(false);
      } else {
        message.success(result.msg || 'Dog restored successfully');
        setRestoreConfirmVisible(false);
        setTimeout(() => {
          onClose();
          onDeleteSuccess?.();
        }, 100);
      }
    } catch (error) {
      message.error(error.data?.msg || 'Error restoring dog');
      console.error('Restore error:', error);
      setRestoreConfirmVisible(false);
    }
  };

  // Helper function to get vaccination status
  const getVaccinationStatus = vaccination_date => {
    if (!vaccination_date) {
      return { status: 'expired', daysUntilExpiration: -999 };
    }

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const expiresAt = new Date(vaccination_date);
      expiresAt.setHours(0, 0, 0, 0);

      if (isNaN(expiresAt.getTime())) {
        return { status: 'expired', daysUntilExpiration: -999 };
      }

      const timeDiff = expiresAt.getTime() - today.getTime();
      const daysUntilExpiration = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

      let status;
      if (daysUntilExpiration < 0) {
        status = 'expired';
      } else if (daysUntilExpiration <= 30) {
        status = 'expiring_soon';
      } else {
        status = 'current';
      }

      return { status, daysUntilExpiration };
    } catch (error) {
      return { status: 'expired', daysUntilExpiration: -999 };
    }
  };

  // Calculate overall vaccination status for the dog
  const calculateOverallVaccinationStatus = () => {
    if (!data?.medical_attachments || data.medical_attachments.length === 0) {
      return { hasExpired: false, hasExpiringSoon: false, hasNoRecords: true };
    }

    let hasExpired = false;
    let hasExpiringSoon = false;
    let expiredCount = 0;
    let expiringSoonCount = 0;

    data.medical_attachments.forEach(attachment => {
      const status = getVaccinationStatus(attachment.vaccination_date);
      if (status.status === 'expired') {
        hasExpired = true;
        expiredCount++;
      } else if (status.status === 'expiring_soon') {
        hasExpiringSoon = true;
        expiringSoonCount++;
      }
    });

    return {
      hasExpired,
      hasExpiringSoon: hasExpiringSoon && !hasExpired,
      hasNoRecords: false,
      expiredCount,
      expiringSoonCount,
    };
  };

  return (
    <Modal
      width={800}
      footer={false}
      open={isOpen}
      onCancel={onClose}
      zIndex={zIndex}
      style={{
        top: 20,
      }}
    >
      <div className="my-8">
        {isLoading ? (
          <Skeleton />
        ) : !data ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Dog information not available</p>
          </div>
        ) : (
          <div>
            {(() => {
              const vacStatus = calculateOverallVaccinationStatus();
              if (vacStatus.hasExpired) {
                return (
                  <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                    <div className="flex items-center">
                      <ExclamationCircleOutlined className="text-red-500 text-xl mr-3" />
                      <div>
                        <p className="text-red-800 font-semibold">
                          ⚠️ Expired Vaccinations
                        </p>
                        <p className="text-red-600 text-sm">
                          This dog has {vacStatus.expiredCount} expired
                          vaccination
                          {vacStatus.expiredCount > 1 ? 's' : ''}. Please update
                          immediately.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              } else if (vacStatus.hasExpiringSoon) {
                return (
                  <div className="mb-4 p-4 bg-orange-50 border-l-4 border-orange-500 rounded">
                    <div className="flex items-center">
                      <WarningOutlined className="text-orange-500 text-xl mr-3" />
                      <div>
                        <p className="text-orange-800 font-semibold">
                          ⚠️ Vaccinations Expiring Soon
                        </p>
                        <p className="text-orange-600 text-sm">
                          This dog has {vacStatus.expiringSoonCount} vaccination
                          {vacStatus.expiringSoonCount > 1 ? 's' : ''} expiring
                          within 30 days.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              } else if (vacStatus.hasNoRecords) {
                return (
                  <div className="mb-4 p-4 bg-gray-50 border-l-4 border-gray-400 rounded">
                    <div className="flex items-center">
                      <ExclamationCircleOutlined className="text-gray-500 text-xl mr-3" />
                      <div>
                        <p className="text-gray-800 font-semibold">
                          ℹ️ No Vaccination Records
                        </p>
                        <p className="text-gray-600 text-sm">
                          This dog has no vaccination records on file.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            <div className="flex items-center justify-between">
              <div>
                <img
                  src={data?.avatar}
                  className="w-36 h-36 rounded-full object-cover"
                  alt="avatar"
                />
                <div className="flex flex-col ml-2 mt-5">
                  <p className="text-3xl font-semibold text-themeBlack">
                    {data?.dog_name || 'Unknown Dog'}
                  </p>
                  <p className="lg text-themeGray-dark ">
                    {data?.breed || 'Unknown breed'},{' '}
                    {data?.gender || 'Unknown gender'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {data?.active === false ? (
                  <button
                    onClick={() => setRestoreConfirmVisible(true)}
                    className="px-4 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors"
                  >
                    Restore Dog
                  </button>
                ) : (
                  <button
                    onClick={() => setDeleteConfirmVisible(true)}
                    className="px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                  >
                    Delete Dog
                  </button>
                )}
                {data?.active !== false && (
                  <ThemeButton
                    content={<p>Edit Dog</p>}
                    className={'w-32'}
                    onClick={onEditDog}
                  />
                )}
              </div>
            </div>
            <p className="mt-3 text-lg font-semibold ml-2">
              About {data?.dog_name || 'this dog'}
            </p>

            <p className="text-[#838383] text-md ml-2 my-2">{data?.about}</p>

            <div className="flex my-2">
              <div className="flex flex-col rounded-md shadow-md w-28 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Weight</p>
                <p className="text-[#2E384D]">{data?.weight || '-'}</p>
                <MedicineBoxOutlined className="text-[#2E384D] mt-1" />
              </div>
              <div className="flex flex-col rounded-md shadow-md w-36 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Date of Birth</p>
                <p className="text-[#2E384D]">
                  {data?.birthday
                    ? dayjs(data?.birthday).format('MMM DD, YYYY')
                    : '-'}
                </p>
                <CalendarOutlined className="text-[#2E384D] mt-1" />
              </div>
              <div className="flex flex-col rounded-md shadow-md w-28 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Age</p>
                <p className="text-[#2E384D]">
                  {data?.birthday
                    ? dayjs().diff(dayjs(data?.birthday), 'year')
                    : '-'}
                </p>
              </div>
              <div className="flex flex-col rounded-md shadow-md w-28 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Color</p>
                <p className="text-[#2E384D]">{data?.color || '-'}</p>
                <GlobalOutlined className="text-[#2E384D] mt-1" />
              </div>
            </div>

            <div className="flex my-2">
              <div className="flex flex-col rounded-md shadow-md w-40 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Added</p>
                {data?.created_at ? (
                  <Tooltip
                    title={dayjs(data.created_at).format(
                      'MMM DD, YYYY [at] h:mm A'
                    )}
                  >
                    <p className="text-[#2E384D] cursor-help">
                      {dayjs(data.created_at).fromNow()}
                    </p>
                  </Tooltip>
                ) : (
                  <p className="text-[#2E384D]">-</p>
                )}
                <CalendarOutlined className="text-[#2E384D] mt-1" />
              </div>
              <div className="flex flex-col rounded-md shadow-md w-40 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Last Updated</p>
                {data?.updated_at ? (
                  <Tooltip
                    title={dayjs(data.updated_at).format(
                      'MMM DD, YYYY [at] h:mm A'
                    )}
                  >
                    <p className="text-[#2E384D] cursor-help">
                      {dayjs(data.updated_at).fromNow()}
                    </p>
                  </Tooltip>
                ) : (
                  <p className="text-[#2E384D]">-</p>
                )}
                <CalendarOutlined className="text-[#2E384D] mt-1" />
              </div>
              <div className="flex flex-col rounded-md shadow-md w-40 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Dog ID</p>
                {data?._id ? (
                  <Tooltip title={data._id}>
                    <p className="text-[#2E384D] cursor-help truncate">
                      {data._id.slice(-8)}
                    </p>
                  </Tooltip>
                ) : (
                  <p className="text-[#2E384D]">-</p>
                )}
                <IdcardOutlined className="text-[#2E384D] mt-1" />
              </div>
            </div>

            <Divider />

            <p className="mt-3 text-lg font-semibold ml-2">Owner</p>
            <div className="flex my-2">
              <div className="flex flex-col rounded-md shadow-md w-52 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Name</p>
                <p
                  className="text-[#2E384D] font-medium cursor-pointer hover:underline text-primary"
                  onClick={() => onViewOwner?.(data?.owner_id)}
                >
                  {data?.owner?.full_name || '-'}
                </p>
                <UserOutlined className="text-[#2E384D] mt-1" />
              </div>
              <div className="flex flex-col rounded-md shadow-md w-52 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Email</p>
                {data?.owner?.email ? (
                  <a
                    href={`mailto:${data.owner.email}`}
                    className="text-[#2E384D] hover:underline"
                  >
                    {data.owner.email}
                  </a>
                ) : (
                  <p className="text-[#2E384D]">-</p>
                )}
              </div>
              <div className="flex flex-col rounded-md shadow-md w-52 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Contact</p>
                <p className="text-[#2E384D]">{data?.owner?.contact || '-'}</p>
              </div>
            </div>

            <Divider />

            <p className="mt-3 text-lg font-semibold ml-2">
              {data?.dog_name || 'Dog'} Behaviors
            </p>
            <div className="flex items-center flex-wrap mt-2">
              {data?.personality?.map((behavior, index) => (
                <div
                  className="px-5 py-1 m-2 border border-[#2E384D] rounded-full"
                  key={index}
                >
                  <p className="text-[#2E384D] text-center">{behavior}</p>
                </div>
              ))}
            </div>

            <Divider />

            <p></p>

            <p className="mt-3 text-lg font-semibold ml-2">Veterinarian</p>
            <div className="flex flex-wrap my-2">
              <div className="flex flex-col rounded-md shadow-md w-28 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Name</p>
                <p className="text-[#2E384D]">
                  {data?.veterinarian?.name || '-'}
                </p>
                <UserOutlined className="text-[#2E384D] mt-1" />
              </div>
              <div className="flex flex-col rounded-md shadow-md w-36 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Phone Number</p>
                {data?.veterinarian?.phone_number ||
                data?.veterinarian?.contact ? (
                  <a
                    href={`tel:${data?.veterinarian?.phone_number || data?.veterinarian?.contact}`}
                    className="text-[#2E384D] hover:underline"
                  >
                    {data?.veterinarian?.phone_number ||
                      data?.veterinarian?.contact}
                  </a>
                ) : (
                  <p className="text-[#2E384D]">-</p>
                )}
              </div>
              {data?.veterinarian?.clinic_name && (
                <div className="flex flex-col rounded-md shadow-md w-40 p-4 justify-center m-2">
                  <p className="text-[#A8ACB1] font-medium">Clinic Name</p>
                  <p className="text-[#2E384D]">
                    {data?.veterinarian?.clinic_name}
                  </p>
                </div>
              )}
              <div className="flex flex-col rounded-md shadow-md w-40 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Location</p>
                <p className="text-[#2E384D]">
                  {data?.veterinarian?.street ||
                  data?.veterinarian?.city ||
                  data?.veterinarian?.state
                    ? `${data?.veterinarian?.street || ''}${
                        data?.veterinarian?.street &&
                        (data?.veterinarian?.city || data?.veterinarian?.state)
                          ? ', '
                          : ''
                      }${data?.veterinarian?.city || ''}${
                        data?.veterinarian?.city && data?.veterinarian?.state
                          ? ', '
                          : ''
                      }${data?.veterinarian?.state || ''}`
                    : '-'}
                </p>
              </div>
            </div>

            <Divider />

            <p className="mt-3 text-lg font-semibold ml-2">Emergency Contact</p>
            <div className="flex my-2">
              <div className="flex flex-col rounded-md shadow-md w-28 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Name</p>
                <p className="text-[#2E384D]">
                  {data?.emergency_contact?.name || '-'}
                </p>
                <UserOutlined className="text-[#2E384D] mt-1" />
              </div>
              <div className="flex flex-col rounded-md shadow-md w-36 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Phone Number</p>
                {data?.emergency_contact?.number ? (
                  <a
                    href={`tel:${data.emergency_contact.number}`}
                    className="text-[#2E384D] hover:underline"
                  >
                    {data.emergency_contact.number}
                  </a>
                ) : (
                  <p className="text-[#2E384D]">-</p>
                )}
              </div>
              <div className="flex flex-col rounded-md shadow-md w-36 p-4 justify-center m-2">
                <p className="text-[#A8ACB1] font-medium">Relationship</p>
                <p className="text-[#2E384D]">
                  {data?.emergency_contact?.relationship || '-'}
                </p>
              </div>
            </div>

            <Divider />

            {data?.medical_problem?.yes && (
              <div>
                <p className="mt-3 text-lg font-semibold ml-2">
                  Medical Problem Detail
                </p>
                <p className="text-sm font-medium ml-2 mt-1 text-[#A8ACB1]">
                  {data?.medical_problem?.details}
                </p>

                <Divider />
              </div>
            )}

            {data?.medication_during_stay?.yes && (
              <div>
                <p className="mt-3 text-lg font-semibold ml-2">
                  Medication During Stay
                </p>
                <p className="text-sm font-medium ml-2 mt-1 text-[#A8ACB1]">
                  {data?.medication_during_stay?.details}
                </p>
                <Divider />
              </div>
            )}

            {data?.regular_medication?.yes && (
              <div>
                <p className="mt-3 text-lg font-semibold ml-2">
                  Regular Medication Detail
                </p>
                <p className="text-sm font-medium ml-2 mt-1 text-[#A8ACB1]">
                  {data?.regular_medication?.details}
                </p>
                <Divider />
              </div>
            )}

            <p className="mt-3 text-lg font-semibold ml-2">Questions</p>
            <div className="flex my-2">
              {data?.questions && (
                <div className="flex flex-col rounded-md shadow-md w-64 p-4 justify-center m-2">
                  <p className="text-[#A8ACB1] font-medium">
                    Attended Daycare Before
                  </p>
                  <p className="text-[#2E384D]">
                    {data?.questions.attended_daycare_before ? 'Yes' : 'No'}
                  </p>
                  <QuestionCircleOutlined className="text-[#2E384D] mt-1" />
                </div>
              )}

              {data?.questions && (
                <div className="flex flex-col rounded-md shadow-md w-64 p-4 justify-center m-2">
                  <p className="text-[#A8ACB1] font-medium">Spayed/Neutered</p>
                  <p className="text-[#2E384D]">
                    {data?.questions.spayed_neutreded ? 'Yes' : 'No'}
                  </p>
                  <QuestionCircleOutlined className="text-[#2E384D] mt-1" />
                </div>
              )}

              {data?.questions && (
                <div className="flex flex-col rounded-md shadow-md w-64 p-4 justify-center m-2">
                  <p className="text-[#A8ACB1] font-medium">Boarded Before</p>
                  <p className="text-[#2E384D]">
                    {data?.questions.boarded_before ? 'Yes' : 'No'}
                  </p>
                  <QuestionCircleOutlined className="text-[#2E384D] mt-1" />
                </div>
              )}

              {data?.questions && (
                <div className="flex flex-col rounded-md shadow-md w-64 p-4 justify-center m-2">
                  <p className="text-[#A8ACB1] font-medium">
                    Socialization Restriction
                  </p>
                  <p className="text-[#2E384D]">
                    {data?.questions.socialization_restriction ? 'Yes' : 'No'}
                  </p>
                  <QuestionCircleOutlined className="text-[#2E384D] mt-1" />
                </div>
              )}
            </div>

            <Divider />

            <div className="flex items-center justify-between mt-3">
              <p className="text-lg font-semibold ml-2">Vaccinations</p>
              <VaccinationStatusBadge
                {...calculateOverallVaccinationStatus()}
              />
            </div>
            <div className="flex flex-wrap mt-2 gap-3">
              {data?.medical_attachments?.map((attachment, index) => {
                const vacStatus = getVaccinationStatus(
                  attachment.vaccination_date
                );

                return (
                  <div
                    key={index}
                    className="flex flex-col rounded-md shadow-md w-64 p-4 m-2 relative"
                  >
                    {vacStatus.status === 'expired' && (
                      <Tag
                        color="error"
                        icon={<ExclamationCircleOutlined />}
                        className="absolute top-2 right-2"
                      >
                        Expired
                      </Tag>
                    )}
                    {vacStatus.status === 'expiring_soon' && (
                      <Tag
                        color="warning"
                        icon={<WarningOutlined />}
                        className="absolute top-2 right-2"
                      >
                        {vacStatus.daysUntilExpiration}d
                      </Tag>
                    )}
                    <div className="mt-2">
                      <p className="text-[#A8ACB1] font-medium text-sm mb-1">
                        Type
                      </p>
                      <p className="text-[#2E384D] font-semibold text-base mb-3">
                        {attachment?.vaccination_title || '-'}
                      </p>
                      <p className="text-[#A8ACB1] font-medium text-sm mb-1">
                        Expiration Date
                      </p>
                      <p className="text-[#2E384D] flex items-center gap-1">
                        <CalendarOutlined className="text-[#2E384D]" />
                        {attachment?.vaccination_date
                          ? dayjs(attachment.vaccination_date).format(
                              'MMM DD, YYYY'
                            )
                          : '-'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Divider />

            <p className="mt-3 text-lg font-semibold ml-2">
              Vaccination Documentation
            </p>
            <div className="flex items-center flex-wrap mt-2">
              {!data?.attachments?.length ? (
                <p className="text-[#A8ACB1] text-sm ml-2">
                  This owner hasn't uploaded any vaccination documentation for{' '}
                  <b>{data?.dog_name || 'this dog'}</b> yet.
                </p>
              ) : (
                data.attachments.map((attachment, index) => (
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-5 py-1 m-2 border border-[#2E384D] rounded-full hover:bg-gray-50"
                    key={index}
                  >
                    {attachment.type === 'image' ? (
                      <img
                        src={attachment.url}
                        alt={attachment.name}
                        className="w-6 h-6 object-cover rounded mr-2"
                      />
                    ) : (
                      <FilePdfOutlined className="text-[#2E384D] mr-2" />
                    )}
                    <p className="text-[#2E384D]">{attachment.name}</p>
                  </a>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Dog"
        open={deleteConfirmVisible}
        onCancel={() => setDeleteConfirmVisible(false)}
        destroyOnClose
        zIndex={(zIndex || 1000) + 10}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteConfirmVisible(false)}
              className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {deleting ? <Spin size="small" /> : 'Delete'}
            </button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-base mb-4">
            Are you sure you want to delete <strong>{data?.dog_name}</strong>?
          </p>
          <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm text-orange-800">
            <p className="font-semibold mb-1">Warning:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Any pending charges for this dog will be lost.</li>
              <li>This action is irreversible.</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* Restore Confirmation Modal */}
      <Modal
        title="Restore Dog"
        open={restoreConfirmVisible}
        onCancel={() => setRestoreConfirmVisible(false)}
        destroyOnClose
        zIndex={(zIndex || 1000) + 10}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setRestoreConfirmVisible(false)}
              className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRestore}
              disabled={restoring}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              {restoring ? <Spin size="small" /> : 'Restore'}
            </button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-base mb-4">
            Are you sure you want to restore <strong>{data?.dog_name}</strong>?
          </p>
          <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
            <p className="font-semibold mb-1">Note:</p>
            <p>
              This will restore the dog and make it available for editing and
              appointment booking again.
            </p>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default DogByIDModal;
